import { useContext, useEffect, useRef } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';
/** BrowserRouter does not expose a blocker. Guard its existing navigator and native
 * history together, without replacing the router or persisting mail drafts. */
export function useEmailNavigationGuard(dirty, pending, discard) {
    const context = useContext(UNSAFE_NavigationContext);
    const latest = useRef({ dirty, pending, discard });
    const internalNavigation = useRef(false);
    latest.current = { dirty, pending, discard };
    useEffect(() => {
        if (!context)
            return;
        const navigator = context.navigator;
        const originals = { push: navigator.push, replace: navigator.replace, go: navigator.go };
        let acceptedIndex = window.history.state?.idx;
        let restoring = false;
        const allow = () => {
            if (restoring)
                return false;
            if (internalNavigation.current)
                return true;
            if (latest.current.pending)
                return false;
            if (!latest.current.dirty)
                return true;
            if (!window.confirm('Je hebt onopgeslagen mailwijzigingen. Wil je deze wijzigingen verlaten?'))
                return false;
            latest.current.dirty = false;
            latest.current.discard();
            return true;
        };
        const push = (...args) => {
            if (!allow())
                return;
            originals.push.apply(navigator, args);
            acceptedIndex = window.history.state?.idx;
        };
        const replace = (...args) => {
            if (!allow())
                return;
            originals.replace.apply(navigator, args);
            acceptedIndex = window.history.state?.idx;
        };
        const go = delta => {
            if (!allow())
                return;
            // BrowserRouter emits popstate asynchronously; MemoryRouter does not.
            // A successful confirmation must not prompt twice for the same traversal.
            originals.go.call(navigator, delta);
        };
        navigator.push = push;
        navigator.replace = replace;
        navigator.go = go;
        const onPop = (event) => {
            const nextIndex = event.state?.idx;
            if (restoring) {
                event.stopImmediatePropagation();
                if (nextIndex === acceptedIndex)
                    restoring = false;
                else if (typeof acceptedIndex === 'number' && typeof nextIndex === 'number')
                    window.history.go(acceptedIndex - nextIndex);
                return;
            }
            if (allow()) {
                acceptedIndex = nextIndex;
                return;
            }
            // Suppress the router event before it unmounts the editor, then return
            // to its history entry. No push/replace means forward history survives.
            if (typeof acceptedIndex === 'number' && typeof nextIndex === 'number' && acceptedIndex !== nextIndex) {
                event.stopImmediatePropagation();
                restoring = true;
                window.history.go(acceptedIndex - nextIndex);
            }
        };
        const onUnload = (event) => {
            if (!latest.current.dirty && !latest.current.pending)
                return;
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('popstate', onPop, true);
        window.addEventListener('beforeunload', onUnload);
        return () => {
            if (navigator.push === push)
                navigator.push = originals.push;
            if (navigator.replace === replace)
                navigator.replace = originals.replace;
            if (navigator.go === go)
                navigator.go = originals.go;
            window.removeEventListener('popstate', onPop, true);
            window.removeEventListener('beforeunload', onUnload);
        };
    }, [context?.navigator]);
    // Only a completed save may select its newly created record while the
    // surrounding refresh is pending. User-driven navigation remains guarded.
    return (navigate) => {
        internalNavigation.current = true;
        try {
            navigate();
        }
        finally {
            internalNavigation.current = false;
        }
    };
}
